import './styles.scss';
import './slick.scss';
import 'aos/dist/aos.css';

import * as React from 'react';
import * as AOS from 'aos';
import Slider, { CustomArrowProps } from 'react-slick';
import { Dispatch } from 'redux';
import { change, FormAction, SubmissionError } from 'redux-form';
import { ResponsiveModalContainer } from '@weddingspot/ws-core-components';

import { VendorsApi } from '@weddingspot/ws-api-client';
import * as analytics from '@weddingspot/ws-analytics';
import MiniForm from './MiniForm';
import { FormData as MiniFormData } from './MiniForm';
import AbandonmentForm from './AbandonmentForm';
import { FormData as AbandonmentFormData } from './AbandonmentForm';
import SignupModal from './SignupModal';
import SuccessModal from './SuccessModal';
import { Global } from '@weddingspot/ws-core-components';
import { ResponsivePlayer } from './ResponsivePlayer';

const { logEvent, AnalyticsEvent } = analytics;

function getImage(name: string) {
    return require(`./../../assets/images/venue-landing/${name}`);
}

const featureUrls = [
    getImage('icon-targate.svg'),
    getImage('icon-megaphone.svg'),
    getImage('icon-page-view.svg'),
    getImage('icon-star.svg'),
];

const testimonialUrls = [getImage('image-testimonial01.png'), getImage('image-testimonial02.png')];

const partnerUrls = [getImage('wedgewood.png'), getImage('clubcorp.png'), getImage('marriott.png')];

const reviewUrls = [getImage('couple-testimonial-01.png'), getImage('couple-testimonial-02.png'), getImage('couple-testimonial-03.png')];

function SampleNextArrow(props: CustomArrowProps) {
    const { className, style, onClick } = props;
    return <div className={className + ' reviews-slider__button reviews-slider__button_right'} onClick={onClick} />;
}

function SamplePrevArrow(props: CustomArrowProps) {
    const { className, style, onClick } = props;
    return <div className={className + ' reviews-slider__button reviews-slider__button_left'} onClick={onClick} />;
}

interface OwnProps {
    showUserSignupModal: () => void;
    shouldShowAbandonModal: () => boolean;
    abandonModalShown?: () => void;
    abandonModalDismiss?: () => void;
    abandonModalAltCta?: () => void;
    abandonFormSuccess?: () => void;
}

interface State {
    signupModalShowing: boolean;
    successModalShowing: boolean;
    signupSubmitted: boolean;
    venueName: string;
    venueEmail: string;
    showAbandonmentModal: boolean;
}

const initialState: State = {
    signupModalShowing: false,
    successModalShowing: false,
    signupSubmitted: false, // use this to reset form after submission
    venueName: '',
    venueEmail: '',
    showAbandonmentModal: false,
};

const PlayButton = () => {
    return (
        <div className='video-button-centering' style={{ paddingBottom: '56.25%' }}>
            <div
                role='button'
                className='play-button'
                title='Play video'
                data-version={1}
                tabIndex={0}
                aria-label='Play video Wedding spot 2022 Video. Opens in a modal'
                style={{ display: 'block', backgroundColor: 'rgb(0, 158, 221)' }}
            >
                <div className='play-button-size' />
                <div className='arrow-size'>
                    <div className='arrow-size-ratio' />
                    <div className='arrow' />
                </div>
            </div>
        </div>
    );
};

class VendorSignup extends React.Component<OwnProps, State> {
    readonly state: State = initialState;

    componentDidMount() {
        AOS.init({
            disable: window.innerWidth < 570,
            duration: 500,
            offset: 200,
        });

        logEvent(AnalyticsEvent.EVTNM_VISITED_VENUE_LANDING, {});

        if (typeof document !== 'undefined' && typeof window !== 'undefined') {
            document.addEventListener('mouseleave', (event: MouseEvent) => {
                // Exit top edge
                if (event.clientY <= 0 && !this.state.signupSubmitted && this.props.shouldShowAbandonModal()) {
                    this.openAbandonmentModal();
                }
            });
        }
    }

    constructor(props: OwnProps) {
        super(props);
        this.openSignupModal = this.openSignupModal.bind(this);
        this.closeSignupModal = this.closeSignupModal.bind(this);
        this.openSuccessModal = this.openSuccessModal.bind(this);
        this.closeSuccessModal = this.closeSuccessModal.bind(this);
        this.openAbandonmentModal = this.openAbandonmentModal.bind(this);
        this.closeAbandonmentModal = this.closeAbandonmentModal.bind(this);
        this.explicitCloseAbandonModal = this.explicitCloseAbandonModal.bind(this);
        this.submitAbandonmentForm = this.submitAbandonmentForm.bind(this);
    }

    openSignupModal(source: string) {
        logEvent(AnalyticsEvent.EVTNM_CLICKED_SIGN_UP_BUTTON, {
            Source: source,
        });
        this.setState({
            signupModalShowing: true,
            signupSubmitted: false,
        });
    }

    closeSignupModal() {
        this.setState({ signupModalShowing: false });
    }

    openSuccessModal() {
        this.setState({
            signupSubmitted: true,
            signupModalShowing: false,
            successModalShowing: true,
        });
    }

    closeSuccessModal() {
        this.setState({ successModalShowing: false });
    }

    openAbandonmentModal() {
        // Trigger event if abandon modal was previously hidden
        if (!this.state.showAbandonmentModal) {
            !!this.props.abandonModalShown && this.props.abandonModalShown();
        }

        this.setState({
            successModalShowing: false,
            signupModalShowing: false,
            showAbandonmentModal: true,
        });
    }

    closeAbandonmentModal() {
        this.setState({ showAbandonmentModal: false });
    }

    explicitCloseAbandonModal() {
        // Trigger event if abandon modal was previously hidden
        if (this.state.showAbandonmentModal) {
            !!this.props.abandonModalDismiss && this.props.abandonModalDismiss();
        }
        this.closeAbandonmentModal();
    }

    render() {
        const sliderSettings = {
            infinite: true,
            speed: 500,
            slidesToShow: 1,
            slidesToScroll: 1,
            className: 'reviews-slider',
            nextArrow: <SampleNextArrow />,
            prevArrow: <SamplePrevArrow />,
            useTransform: false,
            easing: 'swing',
        };

        return (
            <div className='vendor-signup'>
                <Global className='body-vendor-signup' />
                <div className='hero-section section'>
                    <div className='hero-section-content'>
                        <div className='hero-section__title'>Welcome Venues</div>
                        <div className='hero-section__subtitle'>Your go-to source for pre-qualified wedding leads</div>
                        <div className='hero-section__description'>
                            <p>
                                Each year in the U.S., couples spend $100 billion on weddings, and these special events represent{' '}
                                <b>up to 20% of group business for hotels.</b>
                            </p>
                            <p>
                                With more than 200,000 requests processed through Wedding Spot™ each year, Cvent now offers a new way for
                                special event venues and hotels to attract wedding business.
                            </p>
                        </div>
                        <ResponsivePlayer videoUrl='https://play.vidyard.com/5eezi2TN9fkvenh3gXC4FD.jpg' />
                    </div>
                    <div className='hero-section-form'>
                        <MiniForm
                            onChange={(data: MiniFormData, dispatch: Dispatch<FormAction>) => {
                                dispatch(change('SignupForm', 'name', data.name || ''));
                                dispatch(change('SignupForm', 'email', data.email || ''));
                            }}
                            onSubmit={() => this.openSignupModal('Top')}
                        />
                    </div>
                </div>

                {!this.state.signupSubmitted && (
                    <SignupModal
                        showModal={this.state.signupModalShowing}
                        handleModalClosed={this.closeSignupModal}
                        onSubmitSuccess={this.openSuccessModal}
                    />
                )}

                <SuccessModal showModal={this.state.successModalShowing} handleModalClosed={this.closeSuccessModal} />

                <div className='features-section section'>
                    <div className='features-section__title section-title'>Why venues love Wedding&nbsp;Spot</div>
                    <div className='features-grid'>
                        <div className='feature'>
                            <div className='feature__container'>
                                <img src={featureUrls[0]} alt='Venue only directory' className='feature__icon' />
                                <div className='feature__title'>Venue only directory</div>
                                <div className='feature__description'>
                                    Wedding Spot exclusively showcases venues, meaning no distractions or barriers
                                    to couples finding your venue.
                                </div>
                            </div>
                        </div>
                        <div className='feature'>
                            <div className='feature__container'>
                                <img src={featureUrls[1]} alt='Phenomenal SEO' className='feature__icon' />
                                <div className='feature__title'>Phenomenal SEO</div>
                                <div className='feature__description'>
                                    We’ve mastered SEO with high-ranking Google search results to help increase your
                                    venue’s visibility to couples who are beginning their search.
                                </div>
                            </div>
                        </div>
                        <div className='feature'>
                            <div className='feature__container'>
                                <img src={featureUrls[2]} alt='High-intent couples' className='feature__icon' />
                                <div className='feature__title'>High-intent couples</div>
                                <div className='feature__description'>
                                    Couples on Wedding Spot can receive and compare custom price quotes,
                                    giving you more qualified couples and leads.
                                </div>
                            </div>
                        </div>
                        <div className='feature'>
                            <div className='feature__container'>
                                <img src={featureUrls[3]} alt='Unrivaled analytics' className='feature__icon' />
                                <div className='feature__title'>Unrivaled analytics</div>
                                <div className='feature__description'>
                                    Wedding Spot offers the empowering data you need, in the form of easy-to-understand performance reports
                                    and insights into your competition and leads.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='mid-cta-container'>
                    <div className='mid-cta-container__title'>Get your venue listed</div>
                    <div className='mid-cta-container__text'>Convinced your venue is right for Wedding Spot? So are we.</div>
                    <div className='mid-cta-container__button Button' onClick={() => this.openSignupModal('Midpage')}>
                        SIGN UP NOW
                    </div>
                </div>

                <div className='testimonials-section section'>
                    <div className='testimonials-section__title section-title'>What they say about us</div>
                    <div className='testimonial' data-aos='fade-right'>
                        <img src={testimonialUrls[0]} alt='Wedgewood Weddings' className='testimonial__img testimonial__img_pos_left' />
                        <div className='testimonial-quote testimonial-quote_pos_right'>
                            <div className='testimonial-quote__quote testimonial-quote__quote_type_start' />
                            <div className='testimonial-quote__quote testimonial-quote__quote_type_end' />
                            <div className='testimonial-quote__title'>Wedgewood Weddings</div>
                            <div className='testimonial-quote-text'>
                                &#8220;Wedding Spot sends us the most qualified, most motivated leads &#8230;The conversion rate of inquiry
                                to appointment for Wedding Spot was 75% &#8230;{' '}
                                <span className='testimonial-quote-text__accent'>Wedding Spot destroyed the competition!</span>&#8221;
                                <div className='testimonial-quote-text__author' data-dd-privacy="mask" data-dd-action-name="Testimonial's Author">Julia Henning-Silva, Wedgewood Weddings</div>
                            </div>
                        </div>
                    </div>
                    <div className='testimonial' data-aos='fade-left'>
                        <img src={testimonialUrls[1]} alt='The Mountain Terrace' className='testimonial__img testimonial__img_pos_right' />
                        <div className='testimonial-quote testimonial-quote_pos_left'>
                            <div className='testimonial-quote__quote testimonial-quote__quote_type_start' />
                            <div className='testimonial-quote__quote testimonial-quote__quote_type_end' />
                            <div className='testimonial-quote__title'>The Mountain Terrace</div>
                            <div className='testimonial-quote-text'>
                                &#8220;Unlike the leads we get from other wedding sites, couples from Wedding Spot have done their research
                                and are ready to book a wedding.{' '}
                                <span className='testimonial-quote-text__accent'>
                                    Wedding Spot has saved our team a significant amount of time
                                </span>{' '}
                                &#8230; basically conducting the first initial consultation for us and then handing off leads that are
                                vetted.&#8221;
                                <div className='testimonial-quote-text__author' data-dd-privacy="mask" data-dd-action-name="Testimonial's Author">Caren Pearlman, The Mountain Terrace</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='partners-section section'>
                    <div className='partners-section__title subsection-title'>Wedding Spot National Partners</div>
                    <div className='partners-row'>
                        <div className='partner-img-container'>
                            <img src={partnerUrls[0]} alt='Wedgewood Weddings' className='partner-img-container__img' />
                        </div>
                        <div className='partner-img-container'>
                            <img src={partnerUrls[1]} alt='ClubcCorp' className='partner-img-container__img' />
                        </div>
                        <div className='partner-img-container'>
                            <img src={partnerUrls[2]} alt='Marriott' className='partner-img-container__img' />
                        </div>
                    </div>
                </div>

                <div className='reviews-section section' data-aos='fade-up'>
                    <div className='reviews-section__title subsection-title'>Couples also love Wedding Spot</div>
                    <Slider {...sliderSettings}>
                        <div className='review-slide review-slide_first'>
                            <img src={reviewUrls[0]} alt='Slide 1' className='review-slide__img' />
                            <div className='review-slide-quote'>
                                &#8220;It’s embarrassing to call places and realize it’s totally out of our price range! &#8230; Thanks to
                                Wedding Spot, I have not once gone to any spot without knowing what price range I’m getting myself
                                into!&#8221;
                                <div className='review-slide-quote__author' data-dd-privacy="mask" data-dd-action-name="Review's Author">Angelina Delcarmen, Wedding Spot Bride</div>
                            </div>
                        </div>
                        <div className='review-slide'>
                            <img src={reviewUrls[1]} alt='Slide 2' className='review-slide__img' />
                            <div className='review-slide-quote'>
                                &#8220;I was getting frustrated using other sites &#8230; Through Wedding Spot, I found our perfect venue
                                &#8230; which I had never heard of before, and within one week I had it booked.&#8221;
                                <div className='review-slide-quote__author' data-dd-privacy="mask" data-dd-action-name="Review's Author">Sally Phung, Wedding Spot Bride</div>
                            </div>
                        </div>
                        <div className='review-slide'>
                            <img src={reviewUrls[2]} alt='Slide 3' className='review-slide__img' />
                            <div className='review-slide-quote'>
                                &#8220;We actually really loved every single venue because Wedding Spot did such a great job on representing
                                them.&#8221;
                                <div className='review-slide-quote__author' data-dd-privacy="mask" data-dd-action-name="Review's Author">Christopher Mann, Wedding Spot Groom</div>
                            </div>
                        </div>
                    </Slider>
                </div>

                <div className='footer-section section'>
                    <div className='footer-section__title'>Create your venue account</div>
                    <div className='footer-section__description'>
                        Ready to book more wedding business?
                        <br />
                        Get started today!
                    </div>
                    <button className='footer-section__button Button' onClick={() => this.openSignupModal('Bottom')}>
                        SIGN UP FOR WEDDING SPOT
                    </button>
                </div>

                <div className='vendor-signup--abandonment'>
                    <ResponsiveModalContainer
                        show={this.state.showAbandonmentModal}
                        handleClickModalBackground={this.explicitCloseAbandonModal}
                        handleClickModalClose={this.explicitCloseAbandonModal}
                        headerContent={<h3 className='vendor-signup--abandonment--header'>Don't leave yet!</h3>}
                    >
                        <div className='vendor-signup--abandonment--content'>
                            <div className='vendor-signup--abandonment--form'>
                                <AbandonmentForm
                                    onSubmit={this.submitAbandonmentForm}
                                    showUserSignupModal={() => {
                                        this.props.showUserSignupModal();
                                        this.closeAbandonmentModal();
                                        !!this.props.abandonModalAltCta && this.props.abandonModalAltCta();
                                    }}
                                />
                            </div>
                        </div>
                    </ResponsiveModalContainer>
                </div>
            </div>
        );
    }

    private submitAbandonmentForm(data: AbandonmentFormData) {
        const nameTokens = data.name.split(' ');
        let fname = nameTokens[0];
        let lname = nameTokens.splice(1).join(' ') || 'None';

        return VendorsApi.vendorSignupLead({
            first_name: fname,
            last_name: lname,
            email: data.email,
        })
            .then(() => {
                this.closeAbandonmentModal();
                this.openSuccessModal();
                this.props.abandonFormSuccess && this.props.abandonFormSuccess();
            })
            .catch((err) => {
                if (!!err && !!err.detail && !!err.detail.content) {
                    const errPayload = err.detail.content;
                    let errData: Partial<AbandonmentFormData> = {};
                    let nameErrs = [];
                    if (!!errPayload.first_name && errPayload.first_name.length > 0) {
                        nameErrs.push(errPayload.first_name[0]);
                    }
                    if (!!errPayload.last_name && errPayload.last_name.length > 0) {
                        nameErrs.push(errPayload.last_name[0]);
                    }
                    if (nameErrs.length) {
                        errData.name = nameErrs[0];
                    }
                    if (!!errPayload.email && errPayload.email.length > 0) {
                        errData.email = errPayload.email[0];
                    }
                    throw new SubmissionError(errData);
                } else {
                    // Unknown error format
                    throw new SubmissionError({
                        _error: 'An error occurred while attempting to submit your information, please try again later.',
                    });
                }
            });
    }
}

export default VendorSignup;
